import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, FileSearch, Loader2 } from 'lucide-react';
import axios from 'axios';
import SectionHead from '@/components/SectionHead';
import { useLang } from '@/context/LanguageContext';
import { inr } from '@/lib/loan';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ORDER = ['received', 'under_review', 'approved', 'disbursed'];

export default function TrackPage() {
  const { t, lang, locale } = useLang();
  const tr = t.track;
  const [params] = useSearchParams();
  const [ref, setRef] = useState(params.get('ref') || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [upiVpa, setUpiVpa] = useState('');
  const [copiedVpa, setCopiedVpa] = useState(false);

  const lookup = async (id) => {
    if (!id.trim()) return;
    setLoading(true);
    setError('');
    setData(null);
    try {
      const { data: d } = await axios.get(`${API}/loans/${id.trim().toUpperCase()}`);
      setData(d);
    } catch (e) {
      setError(tr.notFound);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    axios
      .get(`${API}/config`)
      .then(({ data: cfg }) => setUpiVpa(cfg.upi_vpa || ''))
      .catch(() => {});
    const q = params.get('ref');
    if (q) lookup(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rejected = data?.status === 'rejected';
  const statusIdx = data ? ORDER.indexOf(data.status) : -1;
  const fmtDate = (iso) => new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(iso));

  return (
    <div className="mx-auto max-w-3xl px-5 sm:px-8" data-testid="track-page">
      <SectionHead eyebrow={t.nav.track} title={tr.title} sub={tr.sub} />

      <form
        className="mt-10 flex flex-col gap-3 sm:flex-row"
        onSubmit={(e) => {
          e.preventDefault();
          lookup(ref);
        }}
      >
        <div className="flex-1">
          <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400">
            {tr.inputLabel}
          </label>
          <input
            data-testid="track-ref-input"
            value={ref}
            onChange={(e) => setRef(e.target.value.toUpperCase())}
            placeholder={tr.placeholder}
            className="w-full rounded-lg border border-white/10 bg-panel/70 px-4 py-3 font-mono text-base font-semibold uppercase tracking-wider text-white placeholder:text-slate-600 outline-none transition-colors focus:border-pulse/70 focus:ring-2 focus:ring-pulse/20"
            aria-label={tr.inputLabel}
          />
        </div>
        <button
          type="submit"
          data-testid="track-check-button"
          disabled={loading}
          className="mt-6 flex items-center justify-center gap-2 rounded-lg bg-pulse px-8 py-3 font-display font-bold text-ink transition-all hover:bg-pulse-dim disabled:opacity-60 sm:mt-0 sm:self-end"
        >
          {loading ? <Loader2 size={17} className="animate-spin" /> : <FileSearch size={17} />}
          {loading ? tr.checking : tr.button}
        </button>
      </form>

      {error && (
        <p data-testid="track-not-found" className="mt-6 rounded-xl border border-red-500/30 bg-red-500/10 px-5 py-4 text-sm text-red-300">
          {error}
        </p>
      )}

      {data && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 rounded-2xl border border-white/10 bg-panel/90 p-6 backdrop-blur-xl sm:p-9"
          data-testid="track-result-card"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="font-mono text-xl font-bold text-pulse" data-testid="track-result-ref">{data.reference_id}</p>
            <span
              data-testid="track-status-badge"
              className={`rounded-full px-4 py-1.5 font-mono text-xs font-bold uppercase tracking-wider ${
                rejected ? 'bg-red-500/15 text-red-300' : 'bg-pulse/15 text-pulse'
              }`}
            >
              {tr.steps[data.status] ? (data.status === 'rejected' ? tr.rejectedTitle : tr.steps[ORDER.indexOf(data.status)] ?? data.status) : data.status}
            </span>
          </div>

          {rejected ? (
            <p className="mt-6 rounded-xl border border-red-500/30 bg-red-500/5 px-5 py-4 text-sm leading-relaxed text-red-300" data-testid="track-rejected-note">
              {tr.rejected}
            </p>
          ) : (
            <div className="mt-8 flex items-start" data-testid="track-timeline">
              {ORDER.map((s, i) => {
                const done = i < statusIdx;
                const current = i === statusIdx;
                return (
                  <div key={s} className="flex flex-1 flex-col items-center text-center last:flex-none">
                    <div className="flex w-full items-center">
                      <span className={`h-0.5 flex-1 ${i === 0 ? 'opacity-0' : done || current ? 'bg-pulse' : 'bg-white/10'}`} />
                      <span
                        data-testid={`track-timeline-step-${i}`}
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 font-mono text-xs font-bold ${
                          done
                            ? 'border-pulse bg-pulse text-ink'
                            : current
                              ? 'border-pulse text-pulse glow-pulse'
                              : 'border-white/15 text-slate-600'
                        }`}
                      >
                        {done ? <Check size={15} strokeWidth={3} /> : i + 1}
                      </span>
                      <span className={`h-0.5 flex-1 ${i === ORDER.length - 1 ? 'opacity-0' : i < statusIdx ? 'bg-pulse' : 'bg-white/10'}`} />
                    </div>
                    <span className={`mt-2 max-w-[90px] text-[11px] font-semibold leading-tight ${done || current ? 'text-white' : 'text-slate-600'}`}>
                      {tr.steps[i]}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-8 grid gap-x-8 gap-y-2.5 border-t border-white/10 pt-6 text-sm sm:grid-cols-2">
            <div className="flex justify-between" data-testid="track-row-applied">
              <span className="text-slate-400">{tr.appliedOn}</span>
              <span className="font-mono font-semibold text-white">{fmtDate(data.created_at)}</span>
            </div>
            <div className="flex justify-between" data-testid="track-row-amount">
              <span className="text-slate-400">{tr.amount}</span>
              <span className="font-mono font-semibold text-white">{inr(data.quote?.amount ?? data.amount)}</span>
            </div>
            <div className="flex justify-between" data-testid="track-row-tenure">
              <span className="text-slate-400">{tr.tenure}</span>
              <span className="font-mono font-semibold text-white">{data.tenure_days} {tr.days}</span>
            </div>
            <div className="flex justify-between" data-testid="track-row-total">
              <span className="text-slate-400">{tr.total}</span>
              <span className="font-mono font-semibold text-pulse">{inr(data.quote?.total_repayment ?? 0)}</span>
            </div>
          </div>

          {data.note && (
            <div className="mt-6 rounded-xl border border-gold/30 bg-gold/5 px-5 py-4" data-testid="track-note">
              <p className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-gold">{tr.note}</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-200">{data.note}</p>
            </div>
          )}

          {data.status === 'approved' && (
            <div className="mt-6 rounded-xl border border-pulse/30 bg-pulse/5 p-5" data-testid="track-approval-checklist">
              <p className="font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-pulse">{tr.checklistTitle}</p>
              <ul className="mt-3 space-y-2.5">
                {tr.checklist.map((c, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed text-slate-200">
                    <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-pulse text-ink">
                      <Check size={11} strokeWidth={3} />
                    </span>
                    {c}
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-xs leading-relaxed text-slate-500">{tr.checklistDocs}</p>
            </div>
          )}

          {data.status === 'disbursed' && (
            <div className="mt-6 rounded-xl border border-pulse/30 bg-pulse/5 p-5" data-testid="track-disbursal-card">
              <p className="font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-pulse">{tr.creditedTitle}</p>
              <div className="mt-3 space-y-2.5 text-sm">
                <div className="flex justify-between" data-testid="track-credited-amount">
                  <span className="text-slate-400">{tr.creditedAmount}</span>
                  <span className="font-mono text-lg font-bold text-pulse">{inr(data.amount)}</span>
                </div>
                <div className="flex justify-between" data-testid="track-repay-on">
                  <span className="text-slate-400">{tr.repayOn}</span>
                  <span className="font-mono font-semibold text-white">
                    {new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(
                      new Date(new Date(data.created_at).getTime() + data.tenure_days * 86400000)
                    )}
                  </span>
                </div>
              </div>
              <p className="mt-3 text-xs leading-relaxed text-slate-500">{tr.repayMethod}</p>
              {upiVpa && (
                <a
                  data-testid="track-upi-pay-button"
                  href={`upi://pay?pa=${encodeURIComponent(upiVpa)}&pn=${encodeURIComponent('PayDay India')}&am=${data.quote?.total_repayment ?? data.amount}&cu=INR&tr=${encodeURIComponent(data.reference_id)}&tn=${encodeURIComponent('Repayment ' + data.reference_id)}`}
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-pulse py-3 font-display font-bold text-ink transition-colors hover:bg-pulse-dim"
                >
                  {tr.payNow}
                </a>
              )}
              {upiVpa && (
                <button
                  type="button"
                  data-testid="track-copy-vpa-button"
                  onClick={() => {
                    navigator.clipboard?.writeText(upiVpa);
                    setCopiedVpa(true);
                    setTimeout(() => setCopiedVpa(false), 1500);
                  }}
                  className="mt-2 w-full rounded-xl border border-white/15 py-2.5 text-xs font-bold text-slate-300 transition-colors hover:border-pulse/50 hover:text-pulse"
                >
                  {copiedVpa ? tr.vpaCopied : `${tr.copyVpa}: ${upiVpa}`}
                </button>
              )}
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}
